import { useState, useEffect, FormEvent } from 'react';
import { RichTextEditor } from './RichTextEditor';

export interface PopupFormData {
  title: string;
  body_content: string;
  cta_text: string;
  cta_url: string;
  target_company_id: string | null;
  start_date: string;
  end_date: string;
}

interface Company {
  id: string;
  usual_name: string;
}

interface PopupFormProps {
  initialData?: Partial<PopupFormData>;
  companies: Company[];
  onSubmit: (data: PopupFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const EMPTY_FORM: PopupFormData = {
  title: '',
  body_content: '',
  cta_text: '',
  cta_url: '',
  target_company_id: null,
  start_date: '',
  end_date: '',
};

export function PopupForm({
  initialData,
  companies,
  onSubmit,
  onCancel,
  isLoading = false,
}: PopupFormProps) {
  const [formData, setFormData] = useState<PopupFormData>({
    ...EMPTY_FORM,
    ...initialData,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof PopupFormData, string>>>({});

  useEffect(() => {
    setFormData({ ...EMPTY_FORM, ...initialData });
  }, [initialData]);

  function validate(): boolean {
    const newErrors: Partial<Record<keyof PopupFormData, string>> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 100) {
      newErrors.title = 'Title must not exceed 100 characters';
    }

    const bodyText = formData.body_content.replace(/<[^>]*>/g, '');
    if (!bodyText.trim()) {
      newErrors.body_content = 'Body content is required';
    } else if (bodyText.length > 2000) {
      newErrors.body_content = 'Body content must not exceed 2000 characters';
    }

    if (!formData.cta_text.trim()) {
      newErrors.cta_text = 'CTA text is required';
    } else if (formData.cta_text.length > 50) {
      newErrors.cta_text = 'CTA text must not exceed 50 characters';
    }

    if (!formData.cta_url.trim()) {
      newErrors.cta_url = 'CTA URL is required';
    } else {
      try {
        new URL(formData.cta_url);
      } catch {
        newErrors.cta_url = 'Please enter a valid URL';
      }
    }

    if (formData.start_date && formData.end_date) {
      if (new Date(formData.end_date) < new Date(formData.start_date)) {
        newErrors.end_date = 'End date must be after start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  }

  function handleFieldChange(field: keyof PopupFormData, value: string | null) {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  }

  return (
    <form className="popup-form" onSubmit={handleSubmit} noValidate>
      <div className="form-group">
        <label htmlFor="popup-title" className="form-label">
          Title <span className="form-required">*</span>
        </label>
        <input
          id="popup-title"
          type="text"
          className={`form-input${errors.title ? ' form-input--error' : ''}`}
          value={formData.title}
          onChange={(e) => handleFieldChange('title', e.target.value)}
          maxLength={100}
          placeholder="Popup title"
          disabled={isLoading}
        />
        <div className="form-hint">{formData.title.length}/100</div>
        {errors.title && <div className="form-error" role="alert">{errors.title}</div>}
      </div>

      <div className="form-group">
        <label className="form-label">
          Body <span className="form-required">*</span>
        </label>
        <RichTextEditor
          value={formData.body_content}
          onChange={(val) => handleFieldChange('body_content', val)}
          maxLength={2000}
          placeholder="Enter popup body content..."
          id="popup-body"
          aria-label="Popup body content"
        />
        {errors.body_content && (
          <div className="form-error" role="alert">{errors.body_content}</div>
        )}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="popup-cta-text" className="form-label">
            CTA Text <span className="form-required">*</span>
          </label>
          <input
            id="popup-cta-text"
            type="text"
            className={`form-input${errors.cta_text ? ' form-input--error' : ''}`}
            value={formData.cta_text}
            onChange={(e) => handleFieldChange('cta_text', e.target.value)}
            maxLength={50}
            placeholder="e.g. Learn more"
            disabled={isLoading}
          />
          <div className="form-hint">{formData.cta_text.length}/50</div>
          {errors.cta_text && <div className="form-error" role="alert">{errors.cta_text}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="popup-cta-url" className="form-label">
            CTA URL <span className="form-required">*</span>
          </label>
          <input
            id="popup-cta-url"
            type="url"
            className={`form-input${errors.cta_url ? ' form-input--error' : ''}`}
            value={formData.cta_url}
            onChange={(e) => handleFieldChange('cta_url', e.target.value)}
            placeholder="https://example.com"
            disabled={isLoading}
          />
          {errors.cta_url && <div className="form-error" role="alert">{errors.cta_url}</div>}
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="popup-target-company" className="form-label">
          Target Company
        </label>
        <select
          id="popup-target-company"
          className="form-select"
          value={formData.target_company_id ?? ''}
          onChange={(e) =>
            handleFieldChange('target_company_id', e.target.value || null)
          }
          disabled={isLoading}
        >
          <option value="">All clients</option>
          {companies.map((company) => (
            <option key={company.id} value={company.id}>
              {company.usual_name}
            </option>
          ))}
        </select>
        <div className="form-hint">Leave as "All clients" to show to everyone</div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="popup-start-date" className="form-label">
            Start Date
          </label>
          <input
            id="popup-start-date"
            type="datetime-local"
            className="form-input"
            value={formData.start_date}
            onChange={(e) => handleFieldChange('start_date', e.target.value)}
            disabled={isLoading}
          />
          <div className="form-hint">Optional. Blank means immediately upon activation.</div>
        </div>

        <div className="form-group">
          <label htmlFor="popup-end-date" className="form-label">
            End Date
          </label>
          <input
            id="popup-end-date"
            type="datetime-local"
            className={`form-input${errors.end_date ? ' form-input--error' : ''}`}
            value={formData.end_date}
            onChange={(e) => handleFieldChange('end_date', e.target.value)}
            disabled={isLoading}
          />
          <div className="form-hint">Optional. Blank means indefinitely until deactivated.</div>
          {errors.end_date && <div className="form-error" role="alert">{errors.end_date}</div>}
        </div>
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={isLoading}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={isLoading}>
          {isLoading ? 'Saving...' : 'Save Popup'}
        </button>
      </div>
    </form>
  );
}
